import { useState } from 'react'

import { Link } from 'react-router-dom'

import {
  Chat,
  ChatParticipant,
  ChatParticipantRole,
  ChatType,
} from '@tribeplatform/gql-client/types'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { T, useStaticIntl } from '../../i18n/index.js'
import { participantRoleFactory } from '../Chat/utils/chat.utils.js'
import { ChatImage } from '../components/ChatImage.js'

import { DemoteModal } from './DemoteModal.js'
import { PromoteModal } from './PromoteModal.js'
import { RemoveModal } from './RemoveModal.js'
import { TransferOwnershipModal } from './TransferOwnershipModal.js'

interface Props {
  chat: Chat
  otherParticipant: ChatParticipant
  onClick: (participant: ChatParticipant) => void
}

export const GroupParticipantsListItem = ({
  otherParticipant,
  chat,
  onClick,
}: Props) => {
  const [openTransferOwnershipModal, setOpenTransferOwnershipModal] =
    useState(false)
  const [openPromoteModal, setOpenPromoteModal] = useState(false)
  const [openDemoteModal, setOpenDemoteModal] = useState(false)
  const [openRemoveModal, setOpenRemoveModal] = useState(false)
  const { currentParticipant } = chat
  const isSelf = currentParticipant?.memberId === otherParticipant.memberId
  const otherParticipantRoleName = useStaticIntl(participantRoleFactory)(
    otherParticipant.role,
  )
  const currentParticipantIsAdmin =
    currentParticipant?.role === ChatParticipantRole.Admin
  const currentParticipantIsOwner =
    currentParticipant?.role === ChatParticipantRole.Owner
  // Staff in the context of group chat not network staff
  const currentParticipantIsStaff =
    currentParticipantIsAdmin || currentParticipantIsOwner
  const otherParticipantIsAdmin =
    otherParticipant.role === ChatParticipantRole.Admin
  const otherParticipantIsOwner =
    otherParticipant.role === ChatParticipantRole.Owner
  const otherParticipantIsStaff =
    otherParticipantIsAdmin || otherParticipantIsOwner
  const canPromote = currentParticipantIsStaff && !otherParticipantIsStaff
  const canDemote =
    !isSelf && currentParticipantIsOwner && otherParticipantIsAdmin
  const canRemove =
    (!isSelf && currentParticipantIsOwner) ||
    (currentParticipantIsAdmin && !otherParticipantIsStaff)
  const canTransferOwnership = currentParticipantIsOwner && !isSelf

  return (
    <>
      <div className="flex items-center justify-between py-1 truncate cursor-pointer">
        <div
          className="flex items-center gap-2"
          onClick={() => onClick(otherParticipant)}
        >
          <ChatImage
            size="1.5x"
            name={otherParticipant.name}
            imageUrl={otherParticipant.avatarUrl}
            type={ChatType.Direct}
          />
          <div className="truncate text-sm font-medium">
            {otherParticipant.name}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 min-w-fit">
          {otherParticipantRoleName && (
            <Badge size="sm" className="self-end">
              {otherParticipantRoleName}
            </Badge>
          )}
          <Dropdown>
            <Dropdown.ButtonMinimal>
              <SvgIcon name="dots-vertical" />
            </Dropdown.ButtonMinimal>
            <Dropdown.Items>
              <Dropdown.Item
                as={Link}
                leadingIcon={<SvgIcon name="member" />}
                target="_blank"
                to={`/member/${otherParticipant.memberId}`}
              >
                <div className="flex items-center justify-between">
                  <T defaultMessage="View profile" id="Messaging.ViewProfile" />
                  <SvgIcon name="link-external" />
                </div>
              </Dropdown.Item>
              {canPromote && (
                <Dropdown.Item
                  leadingIcon={<SvgIcon name="user-up-02" />}
                  onClick={() => setOpenPromoteModal(true)}
                >
                  <T defaultMessage="Promote" id="Generics.Promote" />
                </Dropdown.Item>
              )}
              {canDemote && (
                <Dropdown.Item
                  leadingIcon={<SvgIcon name="user-down-02" />}
                  onClick={() => setOpenDemoteModal(true)}
                >
                  <T defaultMessage="Demote" id="Generics.Demote" />
                </Dropdown.Item>
              )}
              {canRemove && (
                <Dropdown.Item
                  leadingIcon={<SvgIcon name="trash" />}
                  className="text-interactive-destructive"
                  onClick={() => setOpenRemoveModal(true)}
                >
                  <T defaultMessage="Remove" id="Generics.Remove" />
                </Dropdown.Item>
              )}
              {canTransferOwnership && (
                <>
                  <Dropdown.Divider className="menu-divider" />
                  <Dropdown.Item
                    leadingIcon={<SvgIcon name="link" />}
                    className="text-interactive-destructive"
                    onClick={() => setOpenTransferOwnershipModal(true)}
                  >
                    <T
                      defaultMessage="Transfer ownership"
                      id="Messaging.TransferOwnership"
                    />
                  </Dropdown.Item>
                </>
              )}
            </Dropdown.Items>
          </Dropdown>
        </div>
      </div>
      {openTransferOwnershipModal && (
        <TransferOwnershipModal
          participant={otherParticipant}
          open={openTransferOwnershipModal}
          onClose={() => setOpenTransferOwnershipModal(false)}
          chatName={chat.name}
        />
      )}
      {openPromoteModal && (
        <PromoteModal
          participant={otherParticipant}
          open={openPromoteModal}
          onClose={() => setOpenPromoteModal(false)}
        />
      )}
      {openDemoteModal && (
        <DemoteModal
          participant={otherParticipant}
          open={openDemoteModal}
          onClose={() => setOpenDemoteModal(false)}
        />
      )}
      {openRemoveModal && (
        <RemoveModal
          participant={otherParticipant}
          open={openRemoveModal}
          onClose={() => setOpenRemoveModal(false)}
        />
      )}
    </>
  )
}
